import { useState } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';

import ReferralHero from 'modules/Referrals/ReferralHero';
import Testimonial from 'modules/Referrals/Testimonial';
import useReferralShareLink from 'services/member/queries/useReferralShareLink';
import ChevronDownIcon from 'modules/common/components/Icons/ChevronDownIcon';
import ChevronUpIcon from 'modules/common/components/Icons/ChevronUpIcon';
import InformationCircleIcon from 'modules/common/components/Icons/InformationCircleIcon';

/**
 * Demo replacement for /referrals. Prod renders Impact.com's `EmbedSkeleton` for the
 * Rewards / Referrals / Payout area, which needs a real Impact JWT and won't load in the
 * demo. We reuse the prod hero + testimonial (they just need a share link URL) and rebuild
 * the rewards widget natively from mock data.
 */

const STATS = {
    sharelinkClicks: 42,
    inProgressReferrals: 1,
    earnedReferrals: 2,
    earnedAmount: 1200,
};

type ReferralRow = {
    name: string;
    payoutDate: string;
    status: string;
    amount: number;
    available: boolean;
    detail?: {
        availability: string;
        rewardReceivedOn: string;
    };
};

const REFERRALS: ReferralRow[] = [
    {
        name: 'Janine Granados',
        payoutDate: 'Mar 17, 2026',
        status: 'Earned',
        amount: 600,
        available: false,
        detail: { availability: 'Not available', rewardReceivedOn: 'May 18, 2026' },
    },
    {
        name: 'Diego Velasquez',
        payoutDate: 'Feb 02, 2026',
        status: 'Earned',
        amount: 600,
        available: true,
        detail: { availability: 'Available', rewardReceivedOn: 'Feb 12, 2026' },
    },
    { name: 'Priya Shah', payoutDate: '—', status: 'In progress', amount: 0, available: false },
];

const REWARDS_LOG = [
    {
        status: 'Available',
        statusVariant: 'success' as const,
        source: 'Referral to Diego Velasquez',
        reward: 600,
        date: 'Feb 12, 2026',
        note: '',
    },
    {
        status: 'Not available',
        statusVariant: 'warning' as const,
        source: 'Referral to Janine Granados',
        reward: 600,
        date: 'Mar 27, 2026',
        note: 'Processing until May 28, 2026. Payout is then scheduled based on your settings.',
    },
];

const PAYOUT = {
    threshold: 10,
    balance: 0,
    accountMask: '9150',
    w9Active: true,
    w9SubmittedDate: 'May 15, 2026',
};

const fmtUSD = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);

/* ------------------------------ stat cards ------------------------------ */

function StatCard({ label, value }: { label: string; value: string }) {
    return (
        <Box
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                p: 3,
                bgcolor: 'background.paper',
            }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {label}
            </Typography>
            <Typography variant="h4">{value}</Typography>
        </Box>
    );
}

/* --------------------------------- tabs --------------------------------- */

type TabKey = 'referrals' | 'rewards' | 'payout';

function TabBar({ value, onChange }: { value: TabKey; onChange: (t: TabKey) => void }) {
    const tabs: { key: TabKey; label: string }[] = [
        { key: 'referrals', label: 'Referrals' },
        { key: 'rewards', label: 'Rewards' },
        { key: 'payout', label: 'Payout' },
    ];
    return (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={4}>
                {tabs.map((t) => (
                    <Box
                        key={t.key}
                        role="tab"
                        tabIndex={0}
                        aria-selected={value === t.key}
                        onClick={() => onChange(t.key)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onChange(t.key);
                            }
                        }}
                        sx={{
                            py: 1.5,
                            cursor: 'pointer',
                            color: value === t.key ? 'text.primary' : 'text.secondary',
                            fontWeight: value === t.key ? 700 : 500,
                            borderBottom: 2,
                            borderColor: value === t.key ? 'text.primary' : 'transparent',
                            mb: '-1px',
                        }}>
                        {t.label}
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}

const CHIP_VARIANTS = {
    success: { bgcolor: 'success.lightHover', color: 'success.dark' },
    warning: { bgcolor: 'warning.lightHover', color: 'warning.main' },
    default: { bgcolor: 'cement.100', color: 'cement.900' },
} as const;

function StatusChip({ label, severity }: { label: string; severity: keyof typeof CHIP_VARIANTS }) {
    return <Chip label={label} size="small" sx={{ ...CHIP_VARIANTS[severity], fontWeight: 600, height: 22 }} />;
}

/* ------------------------------ referrals tab ------------------------------ */

function ReferralsTab() {
    const [expanded, setExpanded] = useState<string | null>(null);
    return (
        <Box>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1.5fr 1fr 2fr',
                    px: 2,
                    py: 1.5,
                    columnGap: 2,
                }}>
                {['Invited', 'Payout Date', 'Status', 'Amount'].map((h) => (
                    <Typography key={h} variant="caption" fontWeight={700}>
                        {h.toUpperCase()}
                    </Typography>
                ))}
            </Box>
            {REFERRALS.map((row) => {
                const isOpen = expanded === row.name;
                const canExpand = Boolean(row.detail);
                return (
                    <Box key={row.name} sx={{ borderTop: 1, borderColor: 'divider' }}>
                        <Box
                            role={canExpand ? 'button' : undefined}
                            tabIndex={canExpand ? 0 : undefined}
                            aria-expanded={canExpand ? isOpen : undefined}
                            onClick={canExpand ? () => setExpanded(isOpen ? null : row.name) : undefined}
                            onKeyDown={
                                canExpand
                                    ? (e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              setExpanded(isOpen ? null : row.name);
                                          }
                                      }
                                    : undefined
                            }
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '2fr 1.5fr 1fr 2fr',
                                alignItems: 'center',
                                px: 2,
                                py: 2,
                                columnGap: 2,
                                cursor: canExpand ? 'pointer' : 'default',
                            }}>
                            <Typography variant="body2">{row.name}</Typography>
                            <Typography variant="body2">{row.payoutDate}</Typography>
                            <Box>
                                <StatusChip
                                    label={row.status}
                                    severity={row.status === 'Earned' ? 'success' : 'default'}
                                />
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2" sx={{ minWidth: 80 }}>
                                    {fmtUSD(row.amount)}
                                </Typography>
                                {!row.available && row.amount > 0 && (
                                    <StatusChip label="Not available" severity="warning" />
                                )}
                                {canExpand && (
                                    <Box sx={{ ml: 'auto', display: 'flex', color: 'text.secondary' }}>
                                        {isOpen ? (
                                            <ChevronUpIcon fontSize="small" />
                                        ) : (
                                            <ChevronDownIcon fontSize="small" />
                                        )}
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                        {isOpen && row.detail && (
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '2fr 1.5fr 1fr 2fr',
                                    px: 2,
                                    pb: 2,
                                    columnGap: 2,
                                }}>
                                <Box sx={{ gridColumn: '4 / span 1' }}>
                                    <Typography variant="body2">{row.detail.availability}</Typography>
                                    <Typography variant="body2">
                                        Reward received on {row.detail.rewardReceivedOn}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}

/* ------------------------------ rewards tab ------------------------------ */

function RewardsTab() {
    return (
        <Box>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1.5fr 2fr 1fr 1.2fr 2fr',
                    px: 2,
                    py: 1.5,
                    columnGap: 2,
                }}>
                {['Status', 'Source', 'Reward', 'Date received', 'Note'].map((h) => (
                    <Typography key={h} variant="caption" fontWeight={700}>
                        {h.toUpperCase()}
                    </Typography>
                ))}
            </Box>
            {REWARDS_LOG.map((row) => (
                <Box
                    key={`${row.source}-${row.date}`}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1.5fr 2fr 1fr 1.2fr 2fr',
                        alignItems: 'flex-start',
                        px: 2,
                        py: 2,
                        borderTop: 1,
                        borderColor: 'divider',
                        columnGap: 2,
                    }}>
                    <Box>
                        <StatusChip label={row.status} severity={row.statusVariant} />
                        {row.note && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                {row.note}
                            </Typography>
                        )}
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            {row.source.startsWith('Referral') ? 'Referral to' : 'Source'}
                        </Typography>
                        <Typography variant="body2">{row.source.replace('Referral to ', '')}</Typography>
                    </Box>
                    <Typography variant="body2">{fmtUSD(row.reward)}</Typography>
                    <Typography variant="body2">{row.date}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {row.note ? '' : '—'}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

/* ------------------------------- payout tab ------------------------------- */

function PayoutTab() {
    return (
        <Stack spacing={4} sx={{ py: 3 }}>
            <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Payout Information
                </Typography>
                <Box
                    sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 3,
                        bgcolor: 'background.paper',
                        maxWidth: 400,
                    }}>
                    <Typography variant="caption" color="text.secondary">
                        Next payout occurs when balance is USD{PAYOUT.threshold.toFixed(2)}
                    </Typography>
                    <Typography variant="h3" sx={{ my: 1 }}>
                        {fmtUSD(PAYOUT.balance)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Account XXXXXXXX{PAYOUT.accountMask}
                    </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    Your balance may take up to 24 hours to update. Payouts will be sent from our referral program
                    provider, impact.com.
                </Typography>
            </Box>

            <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    W-9 tax form
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <StatusChip label="Active" severity="success" />
                    <Typography variant="body2" color="text.secondary">
                        Submitted on {PAYOUT.w9SubmittedDate}.
                    </Typography>
                </Stack>
                <Button variant="outlined" size="small">
                    Submit new tax form
                </Button>
            </Box>

            <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6">Indirect tax</Typography>
                    <InformationCircleIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    Not registered for indirect tax. If you&apos;ve previously registered with your tax authority,
                    contact our{' '}
                    <Box
                        component="a"
                        href="mailto:support@collective.com"
                        sx={{ color: 'primary.main', textDecoration: 'underline' }}>
                        support team
                    </Box>{' '}
                    to add your information to stay tax compliant.
                </Typography>
            </Box>
        </Stack>
    );
}

/* --------------------------------- widget --------------------------------- */

function DemoRewardsWidget() {
    const [tab, setTab] = useState<TabKey>('referrals');
    return (
        <Box>
            <Typography variant="h2" sx={{ mb: 3 }}>
                Rewards
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                    gap: 2,
                    mb: 4,
                }}>
                <StatCard label="Sharelink clicks" value={String(STATS.sharelinkClicks)} />
                <StatCard label="In progress referrals" value={String(STATS.inProgressReferrals)} />
                <StatCard label="Earned referrals" value={String(STATS.earnedReferrals)} />
                <StatCard label="Earned amount" value={fmtUSD(STATS.earnedAmount)} />
            </Box>
            <TabBar value={tab} onChange={setTab} />
            <Box sx={{ mt: 3 }}>
                {tab === 'referrals' && <ReferralsTab />}
                {tab === 'rewards' && <RewardsTab />}
                {tab === 'payout' && <PayoutTab />}
            </Box>
        </Box>
    );
}

/* --------------------------------- shell --------------------------------- */

export default function DemoReferAndEarn() {
    const { copyUrl, isPending } = useReferralShareLink();
    return (
        <Stack spacing={4} sx={{ width: '100%', maxWidth: 1280, mx: 'auto' }}>
            <ReferralHero copyUrl={copyUrl} referralCode="DEMO2026" isLoading={isPending} />
            <Testimonial />
            <DemoRewardsWidget />
        </Stack>
    );
}
